import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  InputAdornment,
  CircularProgress,
  useTheme,
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import Download from '@mui/icons-material/Download';
import Campaign from '@mui/icons-material/Campaign';
import { useSnackbar } from 'notistack';
import { fetchNewsletters } from '../../store/slices/newsletterSlice';
import * as newsletterApi from '../../api/newsletterApi';
import { getCountries } from '../../utils/countries';

function NewsletterDataContent() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { list: newsletters, loading } = useSelector((s) => s.newsletter);
  const [countries, setCountries] = useState([]);
  const [filters, setFilters] = useState({ country: '', search: '' });

  useEffect(() => {
    dispatch(fetchNewsletters(filters));
  }, [dispatch, filters.country, filters.search]);

  useEffect(() => {
    getCountries().then(setCountries).catch(() => setCountries([]));
  }, []);

  const handleExport = async () => {
    try {
      const blob = await newsletterApi.exportNewsletters(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'newsletter.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      enqueueSnackbar('Export réussi.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Erreur lors de l\'export.', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Campaign sx={{ color: 'primary.main', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Newsletters
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {newsletters.length} inscription{newsletters.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Rechercher par nom, e-mail…"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1 }}
        />
        <TextField
          select
          size="small"
          label="Pays"
          value={filters.country}
          onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Tous les pays</MenuItem>
          {countries.map((c) => (
            <MenuItem key={c.code} value={c.name}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" startIcon={<Download />} onClick={handleExport} disabled={loading}>
          Télécharger Excel
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Nom</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>E-mail</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Pays</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : newsletters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Aucune inscription newsletter.
                  </TableCell>
                </TableRow>
              ) : (
                newsletters.map((n) => (
                  <TableRow key={n._id} hover>
                    <TableCell>{n.fullname}</TableCell>
                    <TableCell>{n.email}</TableCell>
                    <TableCell>{n.country}</TableCell>
                    <TableCell>{n.source || '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default function NewsletterData() {
  const { user } = useSelector((s) => s.auth);
  if (user?.role !== 'admin' && user?.role !== 'moderator') {
    return <Navigate to="/dashboard" replace />;
  }
  return <NewsletterDataContent />;
}
